import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import JobCard from "../components/JobCard";
import JobCardSkeleton from "../components/JobCardSkeleton";
import { EVENT_SPECIALIZATIONS, FREELANCE_SPECIALIZATIONS } from "../data/specializations";
import { MapPin, SlidersHorizontal, X, ChevronLeft, ChevronRight, Briefcase, Sparkles } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 12;

// Active filter chip — shows label + X to remove
function FilterChip({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-brand/10 text-brand border border-brand/20">
      {label}
      <button onClick={onRemove} className="hover:text-red-500 transition">
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

export default function JobsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [locating, setLocating] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Read all filters from URL
  const oppType     = searchParams.get("type") || "";
  const q           = searchParams.get("q") || "";
  const spec        = searchParams.get("specialization") || "";
  const sort        = searchParams.get("sort") || "latest";
  const radius      = searchParams.get("radius_km") || "";
  const lat         = searchParams.get("lat") || "";
  const lng         = searchParams.get("lng") || "";
  const locationName = searchParams.get("location_name") || "";
  const availableOnly = searchParams.get("available_only") === "true";

  const setParam = useCallback((k, v) => {
    const p = new URLSearchParams(searchParams);
    if (v) p.set(k, v); else p.delete(k);
    setPage(1);
    setSearchParams(p);
  }, [searchParams, setSearchParams]);

  const removeParam = useCallback((...keys) => {
    const p = new URLSearchParams(searchParams);
    keys.forEach(k => p.delete(k));
    setPage(1);
    setSearchParams(p);
  }, [searchParams, setSearchParams]);

  const fetchJobs = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        limit: String(PAGE_SIZE + 1),
        skip: String((p - 1) * PAGE_SIZE),
      });
      if (oppType) qs.set("opportunity_type", oppType);
      if (q) qs.set("q", q);
      if (spec) qs.set("specialization", spec);
      if (sort) qs.set("sort", sort);
      if (radius && lat && lng) {
        qs.set("radius_km", radius);
        qs.set("lat", lat);
        qs.set("lng", lng);
      }
      if (availableOnly) qs.set("available_only", "true");
      const { data } = await api.get(`/jobs?${qs.toString()}`);
      setHasMore(data.length > PAGE_SIZE);
      setJobs(data.slice(0, PAGE_SIZE));
    } catch {
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, [oppType, q, spec, sort, radius, lat, lng, availableOnly]);

  // Re-fetch when filters change
  useEffect(() => {
    setPage(1);
    fetchJobs(1);
  }, [oppType, q, spec, sort, radius, lat, lng, availableOnly]); // eslint-disable-line

  // Re-fetch when page changes
  useEffect(() => {
    fetchJobs(page);
  }, [page]); // eslint-disable-line

  const detectLocation = () => {
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        let cityName = "";
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
            { headers: { "Accept-Language": "en" } }
          );
          const d = await res.json();
          const addr = d.address;
          cityName = addr.city || addr.town || addr.village || addr.county || "";
        } catch {}
        const p = new URLSearchParams(searchParams);
        p.set("lat", String(latitude));
        p.set("lng", String(longitude));
        p.set("location_name", cityName);
        if (!p.get("radius_km")) p.set("radius_km", "25");
        p.set("sort", "nearest");
        setSearchParams(p);
        setLocating(false);
        toast.success(cityName ? `Showing jobs near ${cityName}` : "Showing jobs near you");
      },
      () => { toast.error("Could not get location"); setLocating(false); }
    );
  };

  const clearLocation = () => {
    const p = new URLSearchParams(searchParams);
    p.delete("lat"); p.delete("lng");
    p.delete("radius_km"); p.delete("location_name");
    if (p.get("sort") === "nearest") p.set("sort", "latest");
    setPage(1);
    setSearchParams(p);
  };

  const handleSortChange = (val) => {
    if (val === "nearest" && !lat) {
      detectLocation();
    } else {
      setParam("sort", val);
    }
  };

  const specs = oppType === "freelance"
    ? FREELANCE_SPECIALIZATIONS
    : oppType === "event_staffing"
      ? EVENT_SPECIALIZATIONS
      : [...EVENT_SPECIALIZATIONS, ...FREELANCE_SPECIALIZATIONS];

  // Build active filter chips for display
  const activeChips = [];
  if (oppType) activeChips.push({
    label: oppType === "event_staffing" ? "Event Staffing" : "Freelance",
    onRemove: () => removeParam("type"),
  });
  if (spec) activeChips.push({
    label: spec,
    onRemove: () => removeParam("specialization"),
  });
  if (sort && sort !== "latest") activeChips.push({
    label: sort === "highest_payment" ? "Highest pay" : sort === "nearest" ? "Nearest" : sort,
    onRemove: () => setParam("sort", "latest"),
  });
  if (lat) activeChips.push({
    label: locationName ? `📍 ${locationName}` : "📍 Near me",
    onRemove: clearLocation,
  });
  if (radius && lat) activeChips.push({
    label: `Within ${radius} km`,
    onRemove: () => removeParam("radius_km"),
  });
  if (availableOnly) activeChips.push({
    label: "Available slots only",
    onRemove: () => removeParam("available_only"),
  });
  if (q) activeChips.push({
    label: `"${q}"`,
    onRemove: () => removeParam("q"),
  });

  const pageTitle = oppType === "event_staffing"
    ? "Event Staffing"
    : oppType === "freelance"
      ? "Freelance Services"
      : "All Opportunities";

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8" data-testid="jobs-list-page">

      {/* Header */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <span className="label-eyebrow text-muted-foreground">Browse</span>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl tracking-tighter">{pageTitle}</h1>
        </div>
        <button
          onClick={() => setShowFilters(s => !s)}
          className="lg:hidden btn-outline !py-2 !px-3 text-xs relative flex items-center gap-1.5"
          data-testid="toggle-filters"
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filters
          {activeChips.length > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-brand text-white text-[10px] rounded-full flex items-center justify-center">
              {activeChips.length}
            </span>
          )}
        </button>
      </div>

      {/* Active filter chips — visible on all screen sizes */}
      {activeChips.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2 items-center">
          <span className="text-xs text-muted-foreground">Active filters:</span>
          {activeChips.map((chip, i) => (
            <FilterChip key={i} label={chip.label} onRemove={chip.onRemove} />
          ))}
          <button
            onClick={() => {
              setSearchParams(new URLSearchParams(q ? { q } : {}));
              setPage(1);
            }}
            className="text-xs text-muted-foreground hover:text-red-500 underline transition"
          >
            Clear all
          </button>
        </div>
      )}

      <div className="mt-6 grid lg:grid-cols-[260px_1fr] gap-6">

        {/* ── Filters sidebar ── */}
        <aside className={`${showFilters ? "block" : "hidden"} lg:block`} data-testid="filters">
          <div className="space-y-5 lg:sticky lg:top-24">

            {/* Type */}
            <div>
              <p className="label-eyebrow text-muted-foreground mb-2">Type</p>
              <div className="space-y-1">
                {[
                  ["", "All opportunities", null],
                  ["event_staffing", "Event Staffing", <Briefcase className="w-3.5 h-3.5" />],
                  ["freelance", "Freelance", <Sparkles className="w-3.5 h-3.5" />],
                ].map(([v, l, icon]) => (
                  <button
                    key={v}
                    onClick={() => setParam("type", v)}
                    className={`flex items-center gap-2 w-full text-left px-3 py-2 text-sm rounded-sm transition ${
                      oppType === v
                        ? "bg-brand text-white font-medium"
                        : "hover:bg-brand-tint text-foreground"
                    }`}
                  >
                    {icon && <span className={oppType === v ? "text-white" : "text-muted-foreground"}>{icon}</span>}
                    {l}
                  </button>
                ))}
              </div>
            </div>

            {/* Specialization */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="label-eyebrow text-muted-foreground">Specialization</p>
                {spec && (
                  <button onClick={() => removeParam("specialization")} className="text-xs text-brand hover:underline">
                    Clear
                  </button>
                )}
              </div>
              <select
                className="input-base text-sm"
                value={spec}
                onChange={e => setParam("specialization", e.target.value)}
                data-testid="filter-spec"
              >
                <option value="">All specializations</option>
                {specs.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Sort */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="label-eyebrow text-muted-foreground">Sort by</p>
                {sort !== "latest" && (
                  <button onClick={() => setParam("sort", "latest")} className="text-xs text-brand hover:underline">
                    Reset
                  </button>
                )}
              </div>
              <div className="space-y-1">
                {[
                  ["latest", "Latest first"],
                  ["highest_payment", "Highest payment"],
                  ["nearest", `Nearest${!lat ? " (needs location)" : ""}`],
                ].map(([v, l]) => (
                  <button
                    key={v}
                    onClick={() => handleSortChange(v)}
                    className={`flex items-center justify-between w-full text-left px-3 py-2 text-sm rounded-sm transition ${
                      sort === v
                        ? "bg-brand text-white font-medium"
                        : "hover:bg-brand-tint text-foreground"
                    }`}
                  >
                    <span>{l}</span>
                    {sort === v && <span className="text-xs opacity-70">✓</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* Location */}
            <div>
              <p className="label-eyebrow text-muted-foreground mb-2">Location</p>
              {lat ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between px-3 py-2 rounded-sm bg-brand/10 border border-brand/20 text-xs text-brand">
                    <span className="flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" />
                      {locationName || "Near you"}
                    </span>
                    <button onClick={clearLocation} className="hover:text-red-500 transition">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Radius</p>
                    <select
                      className="input-base text-sm"
                      value={radius}
                      onChange={e => setParam("radius_km", e.target.value)}
                      data-testid="filter-radius"
                    >
                      <option value="5">Within 5 km</option>
                      <option value="10">Within 10 km</option>
                      <option value="25">Within 25 km</option>
                      <option value="50">Within 50 km</option>
                    </select>
                  </div>
                </div>
              ) : (
                <button
                  onClick={detectLocation}
                  disabled={locating}
                  className="btn-outline w-full text-xs flex items-center justify-center gap-1.5"
                  data-testid="filter-use-location"
                >
                  <MapPin className="w-3.5 h-3.5" />
                  {locating ? "Detecting…" : "Use my location"}
                </button>
              )}
            </div>

            {/* Available only */}
            <div>
              <p className="label-eyebrow text-muted-foreground mb-2">Availability</p>
              <button
                onClick={() => setParam("available_only", availableOnly ? "" : "true")}
                className={`flex items-center justify-between w-full px-3 py-2 text-sm rounded-sm border transition ${
                  availableOnly
                    ? "bg-brand text-white border-brand font-medium"
                    : "border-border hover:bg-brand-tint text-foreground"
                }`}
              >
                <span>Open slots only</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${availableOnly ? "bg-white/20" : "bg-muted text-muted-foreground"}`}>
                  {availableOnly ? "On" : "Off"}
                </span>
              </button>
              <p className="text-xs text-muted-foreground mt-1">
                Only show jobs with remaining slots
              </p>
            </div>

            {/* Mobile apply button */}
            <button
              onClick={() => setShowFilters(false)}
              className="lg:hidden btn-primary w-full text-sm"
            >
              Show results
            </button>
          </div>
        </aside>

        {/* ── Results ── */}
        <main>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[...Array(6)].map((_, i) => <JobCardSkeleton key={i} />)}
            </div>
          ) : jobs.length === 0 ? (
            <div className="card-flat text-center py-16">
              <p className="font-display text-xl text-foreground">No jobs found</p>
              <p className="text-sm text-muted-foreground mt-1">
                {availableOnly
                  ? "No jobs with open slots match your filters. Try turning off 'Open slots only'."
                  : activeChips.length > 0
                    ? "Try removing some filters to see more results."
                    : "No jobs posted yet in this category."
                }
              </p>
              {activeChips.length > 0 && (
                <button
                  onClick={() => { setSearchParams(new URLSearchParams(q ? { q } : {})); setPage(1); }}
                  className="btn-tint mt-4 text-sm"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <>
              <p className="text-xs text-muted-foreground mb-4">
                {jobs.length}{hasMore ? "+" : ""} result{jobs.length !== 1 ? "s" : ""}
                {activeChips.length > 0 ? " matching your filters" : ""}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {jobs.map(j => <JobCard key={j.id} job={j} />)}
              </div>

              {/* Pagination */}
              {(page > 1 || hasMore) && (
                <div className="mt-8 flex items-center justify-center gap-3">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="btn-outline !py-2 !px-4 disabled:opacity-40 flex items-center gap-1.5 text-sm"
                  >
                    <ChevronLeft className="w-4 h-4" /> Prev
                  </button>
                  <span className="text-sm text-muted-foreground">Page {page}</span>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={!hasMore}
                    className="btn-outline !py-2 !px-4 disabled:opacity-40 flex items-center gap-1.5 text-sm"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
